import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { FiPlusCircle, FiArrowRight, FiEdit2 } from 'react-icons/fi';
import { MdSearch } from 'react-icons/md';
import { Header, CustomPagination } from '../../components';
import { useStateContext } from '../../contexts/ContextProvider';
import useKYCStatusRedirect from '../../hooks/useKYCStatusRedirect';
import { userRequest } from '../../utils/requestMethods';
import { formatCurrency } from '../../utils/currencyUtils';
import { getEducationLevelLabel, getFundEducationLevels } from '../../utils/enrollmentUtils';
import SkeletonTable from '../../components/common/SkeletonTable';
import EnableMultiFundBanner from '../../components/Finance/EnableMultiFundBanner';
import CreateFundModal from '../../components/Finance/CreateFundModal';
import EditFundModal from '../../components/Finance/EditFundModal';

const customStyles = {
  headRow: {
    style: { backgroundColor: 'rgb(248, 250, 252)', fontWeight: 'bold' },
  },
};

const Funds = () => {
  useKYCStatusRedirect('/finance/funds');
  const { school, refreshSchool } = useStateContext();
  const schoolId = school?._id;
  const multiFundEnabled = !!school?.settings?.finance?.multi_fund_enabled;

  const [funds, setFunds] = useState([]);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [totalFunds, setTotalFunds] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [editingFund, setEditingFund] = useState(null);

  const fetchFunds = async (requestedPage = page, requestedPerPage = perPage) => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const { data } = await userRequest.get(
        `/schools/${schoolId}/funds?page=${requestedPage}&limit=${requestedPerPage}`
      );
      const result = data?.data || data || {};
      setFunds(result.funds || []);
      setTotalFunds(result.total_documents || 0);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFunds();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, page, perPage]);

  const columns = [
    {
      name: 'Name',
      cell: row => (
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-800">{row.name}</span>
          {row.is_default && (
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">Default</span>
          )}
        </div>
      ),
    },
    {
      name: 'Educational Level',
      cell: row => {
        const levels = getFundEducationLevels(row);
        return levels.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {levels.map(level => (
              <span key={level} className="text-xs font-medium px-2.5 py-1 rounded-full bg-gray-100 text-gray-600 capitalize">
                {getEducationLevelLabel(level)}
              </span>
            ))}
          </div>
        ) : <span className="text-gray-400 text-sm">—</span>;
      },
    },
    {
      name: 'Available Balance',
      cell: row => (
        <span className="font-semibold text-gray-800">
          {row.balance ? formatCurrency(row.balance.availableBalance) : '—'}
        </span>
      ),
    },
    {
      name: 'Status',
      cell: row => (
        <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${row.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
          {row.is_active ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    {
      cell: row => (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setEditingFund(row)}
            className="p-2 rounded-full text-gray-500 bg-gray-50 hover:bg-gray-100 transition-colors"
            title="Edit fund"
          >
            <FiEdit2 className="w-4 h-4" />
          </button>
          <Link to={`/finance/funds/${row._id}`}>
            <button className="p-2 rounded-full text-indigo-600 bg-indigo-50 hover:bg-indigo-100 transition-colors">
              <FiArrowRight className="w-4 h-4" />
            </button>
          </Link>
        </div>
      ),
      ignoreRowClick: true,
      button: true,
      width: '100px',
    },
  ];

  const filteredData = funds.filter(f =>
    f.name?.toLowerCase().includes(filterText.toLowerCase())
  );

  // A level can belong to only one fund, so map every claimed level to the fund
  // holding it and lock those options in the pickers.
  const levelOwners = useMemo(() => {
    const owners = {};
    funds.forEach(fund => {
      getFundEducationLevels(fund).forEach(level => { owners[level] = fund.name; });
    });
    return owners;
  }, [funds]);

  // When editing, the fund's own levels must stay selectable so they can be
  // kept or removed — only levels held by other funds are off-limits.
  const levelsTakenByOthers = (fund) => {
    const own = new Set(getFundEducationLevels(fund));
    return Object.fromEntries(
      Object.entries(levelOwners).filter(([level]) => !own.has(level))
    );
  };

  return (
    <div className="m-2 md:m-10 mt-24 p-4 md:p-10 dark:bg-secondary-dark-bg bg-white rounded-3xl min-h-screen">
      <div className="flex items-start justify-between mb-6">
        <Header
          category="Finance"
          title="Funds"
          description="Manage dedicated money buckets by educational level or purpose with independent fee routing and balances."
        />
        <button
          type="button"
          disabled={!multiFundEnabled}
          onClick={() => setIsCreateOpen(true)}
          title={!multiFundEnabled ? 'Enable multi-fund to create funds' : ''}
          className="inline-flex items-center gap-2 py-2.5 px-5 text-sm font-medium rounded-full text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiPlusCircle className="w-4 h-4" />
          Create Fund
        </button>
      </div>

      {!multiFundEnabled && (
        <EnableMultiFundBanner schoolId={schoolId} onEnabled={() => { refreshSchool(); fetchFunds(); }} />
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="relative">
          <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
            <MdSearch className="text-gray-400" />
          </div>
          <input
            type="text"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            placeholder="Search funds..."
            className="pl-9 pr-4 py-2 text-sm rounded-full bg-gray-50 border border-gray-200 focus:ring-indigo-500 focus:border-indigo-500 w-48 md:w-64"
          />
        </div>
      </div>

      <div className="border border-gray-200 rounded-2xl overflow-hidden">
        <DataTable
          data={filteredData}
          columns={columns}
          getRowId={row => row._id}
          pagination
          paginationServer
          paginationTotalRows={totalFunds}
          paginationDefaultPage={page}
          paginationPerPage={perPage}
          onChangePage={nextPage => setPage(nextPage)}
          onChangeRowsPerPage={nextPerPage => {
            setPerPage(nextPerPage);
            setPage(1);
          }}
          paginationComponent={CustomPagination}
          paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
          customStyles={customStyles}
          progressPending={loading}
          progressComponent={<SkeletonTable rows={5} columns={5} />}
          highlightOnHover
          persistTableHead
          noDataComponent={
            <div className="py-14 text-center text-sm text-gray-400">No funds found.</div>
          }
        />
      </div>

      {isCreateOpen && (
        <CreateFundModal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          schoolId={schoolId}
          onCreated={fetchFunds}
          disabledLevels={levelOwners}
        />
      )}

      {editingFund && (
        <EditFundModal
          isOpen={!!editingFund}
          onClose={() => setEditingFund(null)}
          schoolId={schoolId}
          fund={editingFund}
          onUpdated={fetchFunds}
          disabledLevels={levelsTakenByOthers(editingFund)}
        />
      )}
    </div>
  );
};

export default Funds;
